import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useHistory, useLocation } from "react-router";
import type {
  Airport,
  FareDetailsV2,
  TripDetailsV2,
} from "@b2bportal/price-freeze-api";
import {
  type FareDetails,
  HalfSheetHashType,
  type TripDetails,
} from "@hopper-b2b/types";
import { AirlineIcon, MobileFlightDetailsModal } from "@hopper-b2b/ui";
import { removeTimezone } from "@hopper-b2b/utilities";
import clsx from "clsx";
import dayjs from "dayjs";

import AirplaneArrive from "./uberAssets/airplane-arrive.svg?react";
import AirplaneDeparture from "./uberAssets/airplane-depart.svg?react";
import Arrow from "./uberAssets/arrow.svg?react";
import "./styles.scss";

const DATE_FORMAT = "ddd, MMM D";
const TIME_FORMAT = "h:mm A";

export interface IFlightItinerary {
  tripDetails: TripDetailsV2;
  fareDetails: FareDetailsV2;
  airports?: { [key: string]: Airport };
  assets?: Record<string, any>;
}

export const FlightItinerary = (props: IFlightItinerary) => {
  const history = useHistory();
  const location = useLocation();
  const [openModal, setOpenModal] = useState(false);
  const [openDepartureModal, setOpenDepartureModal] = useState(true);

  const openDetailedItinerary = useCallback(
    (departure: boolean) => {
      history.push(
        `${location.pathname}${location.search}#${
          departure
            ? HalfSheetHashType.FLIGHT_DETAILS_DEPARTURE
            : HalfSheetHashType.FLIGHT_DETAILS_RETURN
        }`
      );
    },
    [history, location]
  );

  useEffect(() => {
    if (location.hash === `#${HalfSheetHashType.FLIGHT_DETAILS_DEPARTURE}`) {
      setOpenDepartureModal(true);
      setOpenModal(true);
    } else if (
      location.hash === `#${HalfSheetHashType.FLIGHT_DETAILS_RETURN}`
    ) {
      setOpenDepartureModal(false);
      setOpenModal(true);
    } else {
      setOpenModal(false);
    }
  }, [location]);

  const { tripDetails, fareDetails, airports, assets } = props;

  const isRoundTrip = !!tripDetails.slices[1];

  const origin = airports?.[tripDetails.slices[0]?.originCode]
    ? airports[tripDetails.slices[0]?.originCode].cityName
    : tripDetails.slices[0]?.originCode;

  const destination = airports?.[tripDetails.slices[0]?.destinationCode]
    ? airports[tripDetails.slices[0]?.destinationCode].cityName
    : tripDetails.slices[0]?.destinationCode;

  const departureDateTime = removeTimezone(tripDetails.slices[0].departureTime);
  const departureDate = dayjs(departureDateTime).format(DATE_FORMAT);
  const departureTime = dayjs(departureDateTime).format(TIME_FORMAT);

  const returnDateTime = tripDetails.slices[1]?.departureTime
    ? removeTimezone(tripDetails.slices[1]?.departureTime)
    : undefined;
  const returnDate = dayjs(returnDateTime).format(DATE_FORMAT);
  const returnTime = dayjs(returnDateTime).format(TIME_FORMAT);

  const departureAirline =
    tripDetails.slices[0].segmentDetails[0].marketingAirline;
  const returnAirline =
    tripDetails.slices[1]?.segmentDetails[0].marketingAirline;

  return (
    <>
      <div className="itinerary-flight-card">
        <div onClick={() => openDetailedItinerary(true)}>
          <FlightCard
            origin={origin}
            destination={destination}
            date={departureDate}
            time={departureTime}
            airline={departureAirline}
          >
            <AirplaneDeparture className="flight-slice-icon img-icon" />
          </FlightCard>
        </div>
        {isRoundTrip ? (
          <div onClick={() => openDetailedItinerary(false)}>
            <FlightCard
              origin={destination}
              destination={origin}
              date={returnDate}
              time={returnTime}
              airline={returnAirline}
            >
              <AirplaneArrive className="flight-slice-icon img-icon" />
            </FlightCard>
          </div>
        ) : null}
      </div>
      <MobileFlightDetailsModal
        openModal={openModal}
        onClose={() => history.goBack()}
        departure={openDepartureModal}
        tripDetails={tripDetails as unknown as TripDetails}
        fareDetails={fareDetails as unknown as FareDetails}
        title={""}
        buttonText={""}
        className={clsx("checkout-popover", "b2b")}
        airports={airports}
        //TODO: update to use a global context to feed the tenant icons
        assets={assets}
      />
    </>
  );
};

interface FlightCardProps extends PropsWithChildren {
  origin: string;
  destination: string;
  date: string;
  time: string;
  airline: { name: string; code: string };
}

const FlightCard = ({
  origin,
  destination,
  date,
  time,
  airline,
  children,
}: FlightCardProps) => (
  <div className="flight-slice icon-card-container">
    <div className="icon-card-icon">{children}</div>
    <div className="icon-card-details">
      <div className="flight-slice-info">
        <div className="flight-slice-title">
          {origin} to {destination}
        </div>
        <div className="flight-slice-info">{date}</div>
        <div className="flight-slice-info">{time}</div>
        <div className="flight-slice-info airline-info">
          {!!airline && (
            <>
              <AirlineIcon size={"small"} airlineCode={airline.code} />
              <div>{airline.name}</div>
            </>
          )}
        </div>
      </div>
      <div className="flight-slice-arrow-icon">
        <Arrow alt="" className="arrow-icon" />
      </div>
    </div>
  </div>
);
